import React, { useContext, useEffect } from 'react'
import { default as VeneerModal } from '@veneer/core/dist/esm/scripts/modal'
import Button from '@veneer/core/dist/esm/scripts/button'
import useContentStack from '../hooks/useContentStack'
import classes from './Modal.module.scss'
import { VENEER_SECONDARY_APPEARANCE } from '../store/Constants'
import { useAnalyticsAPI } from '../analytics'
import { ConfigContext } from '../store/ConfigContext'
import ButtonGroup from '@veneer/core/dist/esm/scripts/button_group'
import IconWarningAlt from '@veneer/core/dist/esm/scripts/icons/icon_warning_alt'

export const FOOTER_LOCATION_CENTER = 'center'
export const FOOTER_LOCATION_END = 'right'

const Modal = ({
  contentType,
  contentStackOptions,
  type,
  show,
  primaryClick,
  secondaryClick = null,
  analytics,
  footerLocation = FOOTER_LOCATION_END,
  className = '',
  closeButton = false,
  onClose = null,
  children = null,
  showFooter = true,
  outsideDisclaimer = null //html text that is send by who calls the Modal
}) => {
  const { isMobile } = useContext(ConfigContext)
  const { trackScreenDisplay, trackButtonClick } = useAnalyticsAPI(
    analytics.screen,
    true
  )

  const onCloseModal = () => {
    if (typeof onClose === 'function') {
      trackButtonClick(analytics.close_button)
      onClose()
    }
  }

  useEffect(() => {
    if (show) {
      trackScreenDisplay({
        force: true
      })
    }
  }, [show])

  const parseContentStackResponse = (data) => {
    const parsedData = {
      header: data.header,
      primary_button: data.primary_button_text,
      secondary_button: data.secondary_button_text,
      content: []
    }
    for (let item of data.content) {
      if (item?.image && item?.image?.image?.url) {
        parsedData.content.push({
          data: {
            src: item.image.image.url,
            alt: 'Printer Image',
            key: item.image.image.uid
          },
          isImage: true
        })
      } else if (item?.copy) {
        parsedData.content.push({
          data: item.copy.copy,
          metadata: item.copy._metadata.uid,
          isImage: false
        })
      }
    }
    if (data.observation) {
      parsedData.observation = {
        icon: data.observation.icon,
        content: data.observation.content
      }
    }
    return parsedData
  }

  const { pageData } = useContentStack(
    contentType,
    parseContentStackResponse,
    contentStackOptions?.queryType,
    { ...contentStackOptions?.additionalParams, instruction: type }
  )

  if (!pageData) return <></>
  return (
    <VeneerModal
      className={`${classes.modal} ${className}`}
      data-testid={`modal-${type}`}
      align="start"
      show={show}
      onClose={onCloseModal}
      closeButton={closeButton}
      portal
      footer={
        showFooter &&
        (pageData?.primary_button || pageData?.secondary_button) && (
          <ButtonGroup
            className={
              footerLocation === FOOTER_LOCATION_CENTER
                ? classes.footerAlignedCenter
                : classes.footerAlignedEnd
            }
          >
            {pageData?.secondary_button && secondaryClick && (
              <Button
                onClick={() => {
                  trackButtonClick(analytics.secondary_button)
                  secondaryClick()
                }}
                data-testid={`secondary-button-${type}`}
                appearance={VENEER_SECONDARY_APPEARANCE}
              >
                {pageData.secondary_button}
              </Button>
            )}
            {pageData?.primary_button && primaryClick && (
              <Button
                onClick={() => {
                  trackButtonClick(analytics.primary_button)
                  primaryClick()
                }}
                data-testid={`primary-button-${type}`}
              >
                {pageData.primary_button}
              </Button>
            )}
          </ButtonGroup>
        )
      }
    >
      <div data-testid="modal-content">
        <div className={classes.header}>{pageData.header}</div>
        {children ? (
          children({ pageData: pageData.content })
        ) : (
          <div className={classes.content}>
            {pageData.content.map((item, i) => {
              const style = i === 0 ? classes.first : ''
              if (item.isImage && item?.data?.src) {
                return (
                  <span key={i} className={style}>
                    <img
                      src={item.data.src}
                      alt={item.data.alt}
                      key={item.data.key}
                    />
                  </span>
                )
              }
              return (
                <div
                  key={i}
                  className={style}
                  dangerouslySetInnerHTML={{
                    __html: item.data
                  }}
                />
              )
            })}
          </div>
        )}
        {pageData.observation && pageData.observation.content && (
          <div className={classes.observation} data-testid="modal_observation">
            {pageData?.observation?.icon && (
              <img
                alt={'Modal Observation'}
                src={pageData.observation.icon.url}
              />
            )}
            <div
              dangerouslySetInnerHTML={{ __html: pageData.observation.content }}
            />
          </div>
        )}
        {outsideDisclaimer && !isMobile && (
          <div
            className={classes.observation}
            data-testid="modal_outside_disclaimer"
          >
            <IconWarningAlt />
            <div dangerouslySetInnerHTML={{ __html: outsideDisclaimer }} />
          </div>
        )}
      </div>
    </VeneerModal>
  )
}

export default Modal
